import type { ClientPresentationDocument, ClientPresentationNode, ReportSection } from "./domain";

const volatileKeys=new Set([
  "id","createdAt","createdBy","updatedAt","updatedBy","editedAt","editedBy",
  "generatedAt","generatedBy","confirmedAt","confirmedBy","approvedAt","approvedBy",
  "clientDraftApprovedAt","sourceAssessmentConfirmedAt","sourceAssessmentConfirmationId",
  "recordedAt","recordedBy","copiedAt","reviewedAt","reviewedBy","migrationVersion",
]);

function normaliseText(value:string){
  return value.normalize("NFKC").replace(/\r\n?/g,"\n").replace(/[ \t]+/g," ").replace(/\n{3,}/g,"\n\n").trim();
}

function canonical(value:unknown):unknown{
  if(typeof value==="string")return normaliseText(value);
  if(value===null||typeof value!=="object")return value;
  if(Array.isArray(value))return value.map(canonical);
  return Object.fromEntries(
    Object.entries(value as Record<string,unknown>)
      .filter(([key])=>!volatileKeys.has(key))
      .sort(([left],[right])=>left.localeCompare(right))
      .map(([key,item])=>[key,canonical(item)]),
  );
}

function checksum(value:unknown){
  const input=JSON.stringify(canonical(value));
  let result=2166136261;
  for(let index=0;index<input.length;index+=1){
    result^=input.charCodeAt(index);
    result=Math.imul(result,16777619);
  }
  return`client-presentation-${(result>>>0).toString(16).padStart(8,"0")}`;
}

function clientSourcePayload(section:ReportSection){
  const blocks=(section.blocks??[]).filter(block=>block.clientVisible&&block.content.trim()).map(block=>({
    type:block.type,
    title:block.title,
    content:block.content,
    includeInPdf:block.includeInPdf,
    sourceFieldKey:block.sourceFieldKey,
    imageFileKey:block.imageFileKey,
    imageLayout:block.imageLayout,
    alternativeText:block.alternativeText,
  }));
  const structured =
    section.id==="google-understanding"?section.sectionOneWorkflow?.clientDraft:
    section.id==="maps-profile"?section.sectionTwoWorkflow?.clientDraft:
    section.id==="website-review"?section.sectionThreeWorkflow?.clientDraft:
    section.id==="prompt-testing"?section.sectionFourWorkflow?.clientDraft:
    section.id==="overall-findings"?section.sectionFiveWorkflow?.approvedPromptResults:
    section.id==="commercial-gaps"?section.sectionSixWorkflow?.simplifiedClientDraft??section.sectionSixWorkflow?.clientDraft:
    section.id==="competitors"?section.sectionSevenWorkflow?.simplifiedClientDraft??section.sectionSevenWorkflow?.clientDraft:
    section.id==="authority-gaps"?section.sectionEightWorkflow?.simplifiedClientDraft??section.sectionEightWorkflow?.clientDraft:
    section.id==="positioning"?section.sectionNineWorkflow?.simplifiedClientDraft??section.sectionNineWorkflow?.clientDraft:
    section.id==="work-programme"?section.sectionTenWorkflow?.v2ClientDraft??section.sectionTenWorkflow?.clientDraft:
    undefined;
  const supporting =
    section.id==="google-understanding"?(section.sectionOneWorkflow?.sourceEvidence??[]).filter(item=>item.clientVisible&&!item.archived).map(item=>({id:item.id,fileKey:item.fileKey,url:item.url,caption:item.selectedExcerpt,title:item.sourceTitle,layout:item.imageLayout,alternativeText:item.alternativeText})):
    section.id==="maps-profile"?{
      links:(section.sectionTwoWorkflow?.links??[]).filter(item=>item.clientVisible&&!item.archived).map(item=>({id:item.id,url:item.url,type:item.linkType,title:item.sourceTitle})),
      screenshots:(section.sectionTwoWorkflow?.screenshots??[]).filter(item=>item.clientVisible&&!item.archived).map(item=>({id:item.id,fileKey:item.fileKey,caption:item.selectedExcerpt,layout:item.imageLayout,alternativeText:item.alternativeText})),
    }:
    section.id==="website-review"?{
      website:section.sectionThreeWorkflow?.website?.clientVisible?{url:section.sectionThreeWorkflow.website.url}:undefined,
      screenshots:(section.sectionThreeWorkflow?.screenshots??[]).filter(item=>item.clientVisible&&!item.archived).map(item=>({id:item.id,fileKey:item.fileKey,caption:item.selectedExcerpt,layout:item.imageLayout,alternativeText:item.alternativeText})),
    }:
    section.id==="prompt-testing"?section.sectionFourWorkflow?.promptTestingMethodology:
    undefined;
  return{heading:section.heading,blocks,structured,supporting,presentationMappings:section.presentationMappings};
}

export function clientPresentationSourceSignature(section:ReportSection){
  return checksum(clientSourcePayload(section));
}

export function clientPresentationStructureSignature(document:ClientPresentationDocument|undefined){
  if(!document||document.status!=="Approved")return"";
  return checksum({schemaVersion:document.schemaVersion,nodes:document.nodes});
}

export type ClientPresentationResolution="Legacy"|"Formatting needs review"|"Client section ready";

export function resolveClientPresentation(section:ReportSection):{
  resolution:ClientPresentationResolution;
  document?:ClientPresentationDocument;
  current:boolean;
  approved:boolean;
}{
  const document=section.clientPresentation;
  if(!document)return{resolution:"Legacy",current:true,approved:false};
  const current=document.sourceContentSignature===clientPresentationSourceSignature(section);
  if(!current||document.status!=="Approved")return{resolution:"Formatting needs review",document,current,approved:false};
  return{resolution:"Client section ready",document,current:true,approved:true};
}

export function createClientPresentationDraft(
  section:ReportSection,
  nodes:ClientPresentationNode[],
  updatedAt:string,
  updatedBy:string,
):ReportSection{
  return{
    ...section,
    clientPresentation:{
      schemaVersion:2,
      sourceContentSignature:clientPresentationSourceSignature(section),
      nodes,
      status:"Draft",
      updatedAt,
      updatedBy,
    },
  };
}

export function approveClientSectionPresentation(section:ReportSection,approvedAt:string,approvedBy:string):ReportSection{
  const document=section.clientPresentation;
  if(!document||document.sourceContentSignature!==clientPresentationSourceSignature(section))return section;
  return{
    ...section,
    clientPresentation:{
      ...document,
      status:"Approved",
      approvedAt,
      approvedBy,
    },
  };
}

export function clientSectionStudioState(section:ReportSection,wordingApproved:boolean){
  const presentation=resolveClientPresentation(section);
  if(!wordingApproved)return"Client section needs approval" as const;
  if(presentation.resolution==="Formatting needs review")return"Formatting needs review" as const;
  return"Client section ready" as const;
}
